import { invalidateDocumentListData } from "@prismicio/editor-fields"
import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import { useRequest } from "@prismicio/editor-support/Suspense"
import {
  Box,
  Button,
  Dialog,
  DialogActionButton,
  DialogActions,
  DialogCancelButton,
  DialogContent,
  DialogDescription,
  DialogHeader,
  Text,
  Tooltip,
  useAlert,
  useToast,
} from "@prismicio/editor-ui"
import { useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { z } from "zod"

import { staticEditorConfig } from "../../app/editorConfig"
import {
  getReleasePermissions,
  type Release,
  updateReleasesListData,
} from "../../app/services/release"

interface ReleaseDeleteButtonProps {
  release: Release
  size?: "small" | "medium" | "large"
}

export function ReleaseDeleteButton(props: ReleaseDeleteButtonProps) {
  const { release, size = "medium" } = props
  const [dialogOpen, setDialogOpen] = useState(false)

  const { disabled, tooltip } = useDeleteReleaseButton(release)

  return (
    <>
      <Tooltip
        visible={tooltip.visible}
        title={tooltip.title}
        content={tooltip.content}
      >
        <Button
          size={size}
          startIcon="delete"
          color="grey"
          onClick={() => setDialogOpen(true)}
          disabled={disabled}
        >
          Delete
        </Button>
      </Tooltip>

      <ReleaseDeleteConfirmationDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        release={release}
      />
    </>
  )
}

function useDeleteReleaseButton(release: Release) {
  const { canPublish: canPublishRelease } = useRequest(getReleasePermissions, [
    release.id,
  ])

  return {
    disabled: !canPublishRelease,
    tooltip: getTooltip({ canPublishRelease }),
  }
}

interface GetTooltipArgs {
  canPublishRelease: boolean
}

function getTooltip(args: GetTooltipArgs) {
  const { canPublishRelease } = args

  if (!canPublishRelease) {
    return {
      visible: true,
      title: "You can't delete this release",
      content:
        "You are missing publishing rights for some of the documents in the release.",
    }
  }

  return {
    visible: false,
    title: "",
    content: "",
  }
}

interface ReleaseDeleteConfirmationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  release: Release
}

function ReleaseDeleteConfirmationDialog(
  props: ReleaseDeleteConfirmationDialogProps,
) {
  const { open, onOpenChange, release } = props

  const [loading, setLoading] = useState(false)
  const { showAlert } = useAlert()
  const { showToast } = useToast()

  const { search } = useLocation()
  const navigate = useNavigate()

  const queryClient = useQueryClient()

  const releaseLabel = release.label || "..."

  async function onDeleteRelease() {
    setLoading(true)

    try {
      await deleteRelease({ id: release.id })
      updateReleasesListData((releases) => {
        // remove the release from the releases cache
        return releases.filter((_release) => _release.id !== release.id)
      })

      // Deleting a release will result in document status being updated
      void invalidateDocumentListData(queryClient)

      showToast({
        title: "Release has been removed",
      })
      navigate(`/upcoming${search}`, {
        unstable_flushSync: true,
      })
    } catch {
      setLoading(false)
      onOpenChange(false)
      showAlert({
        title: "Error",
        subtitle: `Something went wrong when removing ${releaseLabel}.`,
      })
    }
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open} size="small">
      <DialogHeader
        icon="alert"
        title="Do you really want to delete this release?"
      />

      <DialogDescription asChild>
        <DialogContent>
          <Box padding={16}>
            <Text>You are about to delete the release: {releaseLabel}.</Text>
          </Box>
        </DialogContent>
      </DialogDescription>

      <DialogActions>
        <DialogCancelButton>No</DialogCancelButton>
        <DialogActionButton
          onClick={onDeleteRelease}
          loading={loading}
          color="tomato"
        >
          Yes, I want to remove the release
        </DialogActionButton>
      </DialogActions>
    </Dialog>
  )
}

interface DeleteReleaseArgs {
  id: string
}

function deleteRelease(args: DeleteReleaseArgs) {
  const { id } = args
  const { authStrategy, coreApiBaseUrl } = staticEditorConfig

  return request(new URL(`releases/${id}`, coreApiBaseUrl), {
    method: "DELETE",
    credentials: getRequestCredentials(authStrategy),
    okType: z.unknown(),
  })
}
